import React, { useState } from "react";
import { ShoppingBag, X, Eye, EyeOff, Download, Plus, Trash2 } from "lucide-react";
import fastrageLogo from "./../assets/fastaglogo.png";
import TextInput from "./TextInput";
import PrimaryButton from "./PrimaryButton";
import SideModal from "./SideModal";
import CustomSelectInput from "./SelectInput";
import { vehicleClasses } from "../Utils/Constants";
import { ErrorToast, SuccessToast } from "../Utils/toast";
import { API } from "../Common/Api";

export default function AgentFastagRequest({ onClose }) {
  const [selectedVehicleClasses, setSelectedVehicleClasses] = useState([]);
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [viewingClass, setViewingClass] = useState(null);

  // Handle updating quantity for a selected class
  const handleUpdateQuantity = (index, newQuantity) => {
    const updated = [...selectedVehicleClasses];
    const val = parseInt(newQuantity) || 0;
    updated[index].quantity = val;
    setSelectedVehicleClasses(updated);
  };

  // Add vehicle class to the list automatically
  const handleAutoAddVehicleClass = (vcValue) => {
    const isDuplicate = selectedVehicleClasses.some(
      (vc) => vc.vehicleClass === vcValue
    );

    if (isDuplicate) return;

    // Add to list with default quantity of 10
    setSelectedVehicleClasses([
      ...selectedVehicleClasses,
      {
        vehicleClass: vcValue,
        quantity: 10,
      },
    ]);
  };

  // Remove vehicle class from list
  const handleRemoveVehicleClass = (index) => {
    setSelectedVehicleClasses(
      selectedVehicleClasses.filter((_, i) => i !== index)
    );
  };

  // Calculate total quantity
  const getTotalQuantity = () => {
    return selectedVehicleClasses.reduce((sum, vc) => sum + vc.quantity, 0);
  };

  const validateForm = () => {
    const newErrors = {};

    if (selectedVehicleClasses.length === 0) {
      newErrors.vehicleClasses = "Please add at least one vehicle class";
    }

    // Validate that all quantities are positive numbers
    const hasInvalidQuantity = selectedVehicleClasses.some(vc => vc.quantity <= 0);
    if (hasInvalidQuantity) {
      newErrors.vehicleClasses = "All quantities must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBuy = async () => {
    if (!validateForm()) return;

    // Create JSON payload for agent FASTag request
    const requestPayload = {
      vehicleClasses: selectedVehicleClasses.map((vc) => ({
        vehicleClass: vc.vehicleClass,
        quantity: String(vc.quantity), // Backend expects string
      })),
    };

    try {
      const response = await API.PurchageFastagAgentRequest(
        JSON.stringify(requestPayload)
      );

      if (response.status === 200 || response.status === 201) {
        SuccessToast(
          response.message || "FASTag request submitted successfully!"
        );
        onClose();
      } else {
        ErrorToast(response.message || "Failed to submit FASTag request");
      }
    } catch (error) {
      console.error("Error submitting FASTag request:", error);
      ErrorToast("Failed to submit FASTag request. Please try again.");
    }
  };

  const handleViewClass = (vehicleClass) => {
    const classData = vehicleClasses.find((vc) => vc.value === vehicleClass);
    // Toggle modal - if same class is clicked, close it; otherwise open new one
    if (modalOpen && viewingClass?.value === vehicleClass) {
      setModalOpen(false);
      setViewingClass(null);
    } else {
      setViewingClass(classData);
      setModalOpen(true);
    }
  };

  const handleDownload = () => {
    if (viewingClass) {
      const link = document.createElement("a");
      link.href = viewingClass.pdf;
      link.download = "vehicle_class.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClose = (e) => {
    setModalOpen(false);
    onClose(e);
  };

  // Get available vehicle classes (not yet selected)
  const availableVehicleClasses = vehicleClasses.filter(
    (vc) =>
      !selectedVehicleClasses.some((svc) => svc.vehicleClass === vc.value)
  );

  return (
    <SideModal
      title="Raise FASTag Request"
      icon={ShoppingBag}
      onClose={handleClose}
      setModalOpen={setModalOpen}
    >
      {/* Vehicle Class Selection Section */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-800 mb-3">
          Select Vehicle Classes <span className="text-red-500">*</span>
          <span className="ml-2 font-normal text-gray-500">(Click to add)</span>
        </label>

        {/* Available Classes Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {availableVehicleClasses.length > 0 ? (
            availableVehicleClasses.map((vc) => (
              <div
                key={vc.value}
                onClick={() => handleAutoAddVehicleClass(vc.value)}
                className="group cursor-pointer bg-white border border-gray-200 rounded-xl p-3 flex flex-col items-center gap-2 hover:border-green-500 hover:bg-green-50 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <div className="relative">
                  <img src={vc.tagimage} alt={vc.value} className="w-12 h-auto transition-transform group-hover:scale-110" />
                  <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-[12px] font-bold text-gray-900 leading-tight">{vc.value}</p>
                  <p className="text-[10px] text-gray-500 leading-tight truncate w-full max-w-[80px]">{vc.name.split(':')[1]?.trim() || vc.name}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-4 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <p className="text-xs text-gray-500 font-medium">All vehicle classes selected</p>
            </div>
          )}
        </div>

        {/* Selected Vehicle Classes List */}
        {selectedVehicleClasses.length > 0 && (
          <div className="bg-white rounded-xl p-4 space-y-3 border border-blue-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-800">Selected Classes</p>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                {selectedVehicleClasses.length} {selectedVehicleClasses.length === 1 ? 'Class' : 'Classes'}
              </span>
            </div>
            
            <div className="space-y-2">
              {selectedVehicleClasses.map((vc, index) => {
                const classData = vehicleClasses.find(
                  (c) => c.value === vc.vehicleClass
                );
                return (
                  <div
                    key={index}
                    className="bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-md hover:scale-105 transition-all duration-200 w-full overflow-hidden"
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full">
                      {/* Vehicle Icon and Info */}
                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="flex-shrink-0">
                          <img
                            src={classData?.tagimage}
                            alt={classData?.value}
                            className="w-10 h-auto"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">
                            {classData?.value}
                          </p>
                        </div>
                      </div>

                      {/* Quantity Editor and Action Buttons */}
                      <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto mt-2 sm:mt-0 flex-shrink-0">
                        {/* Quantity Editor */}
                        <div className="flex flex-row items-center gap-2">
                          <label className="text-[10px] font-medium text-gray-500">Quantity</label>
                          <input
                            type="number"
                            value={vc.quantity}
                            onChange={(e) => handleUpdateQuantity(index, e.target.value)}
                            min="1"
                            step="1"
                            className="w-16 h-8 text-center text-sm font-bold border-2 border-gray-100 rounded-md focus:border-blue-400 focus:outline-none transition-colors"
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewClass(vc.vehicleClass)}
                            className="p-2 text-black-400 rounded-lg transition-colors"
                            title="View Details"
                          >
                            {modalOpen && viewingClass?.value === vc.vehicleClass ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleRemoveVehicleClass(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total Summary */}
            <div className="border-t-2 border-gray-300 pt-3 mt-3">
              <div className="flex justify-between items-center bg-white rounded-lg p-3 shadow-sm">
                <span className="font-bold text-gray-900">Total:</span>
                <div className="text-right">
                  <p className="font-bold text-xl text-gray-900">
                    {getTotalQuantity()} FASTags
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {errors.vehicleClasses && (
          <p className="text-red-500 text-xs mt-2 font-medium">{errors.vehicleClasses}</p>
        )}
      </div>

      <PrimaryButton
        onClick={handleBuy}
        className="!mt-[30px] border border-[#EE3B31] !text-white bg-[#FEF5F4] hover:bg-red-50 hover:!text-white"
      >
        Submit Request
      </PrimaryButton>

      <div className="mt-[20px] w-full flex justify-center">
        <img src={fastrageLogo} alt="Fastag" className="h-6" />
      </div>

      {/* === Modal Popup === */}
      {modalOpen && viewingClass && (
        <div className="">
          <div className="block bg-white rounded-[20px] shadow-lg w-full max-w-xl p-6 position-btn">
            <div className="absolute top-4 right-4 gap-3 flex-row flex">
              <button
                onClick={handleDownload}
                className=" text-gray-600 hover:text-red-500"
              >
                <Download size={20} />
              </button>
              <button
                onClick={() => setModalOpen(false)}
                className=" text-gray-600 hover:text-red-500"
              >
                <X size={20} />
              </button>
            </div>
            <h2 className="text-xl font-semibold mb-[4px] text-center">
              {viewingClass.value}
            </h2>
            <p className="mb-[30px] text-sm text-center">{viewingClass.description}</p>
            <div className="relative w-full flex justify-center items-center py-8 px-4">
              <div className="relative bg-white p-8 rounded-2xl shadow-2xl border border-gray-200 max-w-2xl w-full">
                {/* Decorative corner accents */}
                <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-red-500 rounded-tl-2xl opacity-20"></div>
                <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-red-500 rounded-br-2xl opacity-20"></div>

                {/* Image container with classic frame */}
                <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-gray-300 shadow-inner">
                  <img
                    src={viewingClass.cardWebp}
                    alt={viewingClass.name}
                    className="w-full h-auto object-contain rounded-lg transition-transform duration-500 hover:scale-[1.12]"
                  />
                </div>

                {/* Elegant bottom accent */}
                <div className="mt-6 flex justify-center">
                  <div className="h-1 w-24 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </SideModal>
  );
}
